// Modal for /dream — fires POST .../dream on mount, shows path + filesystem
// link + preview when the consolidation lands (or an error with Retry).
import { useClient } from '@cortex/react-query';
import type { PersistentAgentDreamResult } from '@cortex/types';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { routes } from '../../../lib/constants';
import { Modal } from '../../common/Modal';

export function DreamModal({
  agentId,
  threadId,
  focus,
  onClose,
}: {
  agentId: string;
  threadId: string;
  focus: string;
  onClose: () => void;
}) {
  const client = useClient();
  const started = useRef(false);

  const dream = useMutation({
    mutationFn: () =>
      client.http
        .post(`v1/persistent-agent-groups/${agentId}/agents/${threadId}/dream`, {
          json: { focus: focus || undefined },
        })
        .json<PersistentAgentDreamResult>(),
  });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    dream.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
  }, []);

  return (
    <Modal
      open={true}
      onClose={onClose}
      eyebrow="Dream"
      title="Consolidate thread memory"
      width={520}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <button className="btn btn--ghost btn--sm" onClick={onClose} type="button">
            Close
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {dream.isPending && (
          <p className="t-xs t-muted" style={{ margin: 0 }}>
            Dreaming — consolidating this thread&rsquo;s context…
          </p>
        )}

        {dream.isError && (
          <>
            <div className="alert alert--danger">
              {String((dream.error as Error)?.message ?? 'Dream failed')}
            </div>
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => dream.mutate()}
              disabled={dream.isPending}
            >
              Retry
            </button>
          </>
        )}

        {dream.isSuccess && dream.data && (
          <>
            <div>
              <div className="field-label" style={{ marginBottom: 4 }}>
                Written to
              </div>
              <code className="t-xs t-mono" style={{ wordBreak: 'break-all' }}>
                {dream.data.path}
              </code>
            </div>
            <div>
              <div className="field-label" style={{ marginBottom: 4 }}>
                Filesystem
              </div>
              <Link
                to={routes.orchestrationFilesystem(dream.data.filesystemId)}
                style={{ color: 'var(--text-link)' }}
              >
                {dream.data.filesystemName}
              </Link>
            </div>
            {dream.data.filesystemCreated && (
              <p className="t-xs t-muted" style={{ margin: 0 }}>
                Created and attached filesystem &ldquo;{dream.data.filesystemName}&rdquo; — threads
                mount it on the thread’s next build.
              </p>
            )}
            {dream.data.preview && (
              <div
                className="t-xs t-muted"
                style={{
                  margin: 0,
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-md, 6px)',
                  background: 'var(--surface-2, var(--surface))',
                  border: '1px solid var(--border)',
                  whiteSpace: 'pre-wrap',
                  maxHeight: 180,
                  overflow: 'auto',
                }}
              >
                {dream.data.preview}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
